import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { Input } from '../../ui/input';
import { TableHead, TableHeader, TableRow } from '../../ui/table';
import { sortBy, type JudgeFilters, type JudgeSortColumn } from './judge-blocks';

interface JudgeFiltersProps {
  filters: JudgeFilters;
  onChange: (filters: JudgeFilters) => void;
}

/** The search box over a judge's table: name, surname, team or address. */
export function JudgeSearch({ filters, onChange, placeholder }: JudgeFiltersProps & { placeholder: string }) {
  return (
    <div className="relative mb-4">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={filters.query}
        onChange={(e) => onChange({ ...filters, query: e.target.value })}
        className="pl-10"
        data-testid="judge-search"
      />
    </div>
  );
}

/** Name, surname, team: the columns, each one a sort. */
export function JudgeSortHeader({ filters, onChange }: JudgeFiltersProps) {
  const { t } = useTranslation();

  const head = (column: JudgeSortColumn, label: string) => (
    <TableHead
      className="cursor-pointer select-none"
      data-testid={`sort-by-${column}`}
      onClick={() => onChange(sortBy(filters, column))}
    >
      <span className="inline-flex items-center">
        {label}
        {filters.column !== column ? (
          <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />
        ) : filters.order === 'asc' ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )}
      </span>
    </TableHead>
  );

  return (
    <TableHeader>
      <TableRow>
        {head('name', t('judgeBoulder.name', { defaultValue: 'Name' }))}
        {head('surname', t('judgeBoulder.surname', { defaultValue: 'Surname' }))}
        {head('team', t('judgeBoulder.team', { defaultValue: 'Team' }))}
      </TableRow>
    </TableHeader>
  );
}
